import BaseModel from './BaseModel';

interface BurndownDay {
    day: string;
    idealEffort: number;
    idealEstimate: number;
    realEffort: number;
    realEstimate: number;
    tracked: number;
}

interface LeadTimeStats {
    avg: number | null;
    median: number | null;
    p90: number | null;
    count: number;
}

/** Class representing a statistics. */
class Report extends BaseModel {
    changes: {
        estimated_hours: number;
        assigned_hours: number;
        deployed_hours: number;
        to_deploy_hours: number;

        nb_assigned: number;
        nb_deployed: number;
        nb_estimated: number;

        billable_estimated_hours: number;
        billable_assigned_hours: number;
        billable_deployed_hours: number;
        billable_to_deploy_hours: number;

        deployment_ratio: number;
        assignment_ratio: number;

        tracked: {
            nb_tracked: number;
            estimated_hours: number;
            tracked_hours: number;
            billable_tracked_hours: number;
            tracking_ratio: number;
        },
        type: {
            bugs: {
                assignment_ratio: number;
                deployment_ratio: number;
                nb_assigned: number;
                nb_deployed: number;
                nb_estimated: number;
            },
            features: {
                assignment_ratio: number;
                deployment_ratio: number;
                nb_assigned: number;
                nb_deployed: number;
                nb_estimated: number;
            },
            other: {
                assignment_ratio: number;
                deployment_ratio: number;
                nb_assigned: number;
                nb_deployed: number;
                nb_estimated: number;
            }
        },
    };

    tracks: {
        average_hours_per_track: number;
        nb: number;
        tracked_hours: number;
    }

    users: {
        nb: number;
        worked_hours: number;
        remaining_hours: number;
        worked_man_days: number;
        remaining_man_days: number;
        tracking_ratio: number;
    };

    activity?: {
        tracked_hours: number;
        nb: number;
        average_hours_per_track: number | null;
        by_domain: { dev: number; non_dev: number };
        by_subtype: Record<string, number>;
        billable_hours: number;
        non_billable_hours: number;
        top_projects: { key: string; hours: number }[];
        top_clients: { key: string; hours: number }[];
        top_changes: { key: string; hours: number }[];
    };

    capacity?: {
        users_nb: number;
        weekdays: number;
        worked_days: number;
        capacity_hours: number;
        tracked_hours: number;
        fill_ratio: number | null;
        remaining_hours: number | null;
    };

    delivery?: {
        nb: number;
        nb_assigned: number;
        nb_deployed: number;
        estimated_hours: number;
        assigned_hours: number;
        deployed_hours: number;
        to_deploy_hours: number;
        billable_estimated_hours: number;
        billable_assigned_hours: number;
        billable_deployed_hours: number;
        billable_to_deploy_hours: number;
        assignment_ratio: number | null;
        deployment_ratio: number | null;
        by_domain: { dev: number; non_dev: number };
    };

    tickets?: {
        volumes: {
            created: number;
            accepted: number;
            discarded: number;
            resolved: number;
            assigned: number;
            merged: number;
            refuse_ratio: number | null;
            net_intake: number;
            resolved_without_estimate: number;
            resolved_without_estimate_ratio: number | null;
        };
        by_day: {
            day: string;
            created: number;
            accepted: number;
            discarded: number;
            resolved: number;
        }[];
        stock: {
            open: number;
            in_triage: number;
            unassigned: number;
            assigned: number;
            wip: number;
            aging_over_7_days: number;
            aging_over_30_days: number;
            assignment_ratio: number | null;
        };
        assignment_flow_ratio: number | null;
        lead_times: {
            create_to_assign: LeadTimeStats;
            create_to_start: LeadTimeStats;
            create_to_resolve: LeadTimeStats;
            assign_to_resolve: LeadTimeStats;
        };
    };

    billing?: {
        billable_estimated_hours: number;
        billable_tracked_hours: number;
        non_billable_tracked_hours: number;
        tracked_vs_estimated_ratio: number | null;
    };

    support?: {
        pendingTriage?: { count: number; ageHoursAvg: number | null; ageHoursMax: number | null };
        createdToday?: { count: number };
        unanswered?: { count: number; thresholdHours: number };
    };

    burndown: Record<string, BurndownDay>;

    burndownMarkers?: {
        milestones: { name: string; day: string }[];
        phases: { name: string; startDay: string; stopDay: string }[];
    };

    constructor(properties: AnyObj) {
        super(properties);

        this.changes  = properties.changes;
        this.tracks   = properties.tracks;
        this.users    = properties.users;
        this.burndown = properties.burndown;
        this.burndownMarkers = properties.burndownMarkers;
        this.activity = properties.activity;
        this.capacity = properties.capacity;
        this.delivery = properties.delivery;
        this.tickets  = properties.tickets;
        this.billing  = properties.billing;
        this.support  = properties.support;
    }
}

export default Report;
